import { faSquareUpRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import {
  Autocomplete,
  BottomNavigation,
  Box,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography
} from "@mui/material"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import { DatePicker } from "@mui/x-date-pickers"
import { useApiGet } from "auth/useApi2"
import Button from "components/Button"
import PaperBox from "components/PaperBox"
import theme from "css/theme/light"
import { Formik } from "formik"
import { allRoutes } from "generated/routes"
import { t } from "i18next"
import moment from "moment"
import { equipmentValidationSchema } from "pages/systemadmin/pages/devices/components/validationSchema"
import { useEffect, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { apiEndpoints } from "generated/apiEndpoints"

export const EquipmentForm = ({
  onSubmit,
  editEquipment,
  result,
  feedback,
  companyResult,
  storagesResult,
  devicesResult,
  externalDeviceResult,
  equipmentStatuses,
  setNoExternalDeviceId
}) => {
  const editMode = editEquipment ? true : false
  const [selectedSwitch, setSwitch] = useState(false)
  const [externalDeviceId, setExternalDeviceId] = useState("")
  const formRef = useRef()
  const [externalConfigsResult, getExternalConfigs] = useApiGet()

  const shouldCreate = !!editMode && !editEquipment?.externalDeviceId

  useEffect(() => {
    if (shouldCreate) {
      setNoExternalDeviceId(true)
    }
  }, [shouldCreate, setNoExternalDeviceId])

  const baseValues = {
    equipmentId: "",
    companyId: 0,
    status: "",
    equipmentTypeId: null,
    subscriptionTime: null,
    equipmentDescription: "",
    mountedOnDeviceId: null,
    mountedOnStorageId: null,
    hasConnectToDevice: false,
    supplements: [],
    externalDeviceId: null,
    ioPriority: true,
    positionPriority: true
  }

  const initialValues = editEquipment
    ? shouldCreate
      ? {
          ...editEquipment,
          externalDeviceId: null
        }
      : {
          ...editEquipment
        }
    : { ...baseValues }

  useEffect(() => {
    if (externalDeviceId !== "") {
      getExternalConfigs(
        apiEndpoints.systemadmindeviceregistry.listexternaldeviceregistryconfigurations.replace(
          "{deviceTypeId}",
          externalDeviceId
        )
      )
    }
  }, [externalDeviceId, getExternalConfigs])

  useEffect(() => {
    initialValues.mountedOnDeviceId ? setSwitch(true) : setSwitch(false)
  }, [initialValues.mountedOnDeviceId])

  const toggleSwitch = (setFieldValue) => {
    setSwitch(!selectedSwitch)
    setFieldValue("mountedOnDeviceId", null)
    setFieldValue("mountedOnStorageId", null)
  }

  const handleSubmit = async (values) => {
    onSubmit(values)
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnChange={false}
      validationSchema={equipmentValidationSchema}
      innerRef={formRef}>
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item md={8}>
              <PaperBox>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h4">{t("global.settings", "Settings")}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      id="externalDeviceTypeId"
                      options={externalDeviceResult?.data}
                      getOptionLabel={(option) => (option.name ? option.name : "")}
                      value={
                        externalDeviceResult?.data?.find((i) => i.id === values.externalDeviceTypeId) || ""
                      }
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id
                      }}
                      onChange={(e, value) => {
                        setFieldValue("externalDeviceTypeId", value?.id)
                        setExternalDeviceId(value?.id || "")
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("global.equipment_type", "Equipment Type")}
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.externalDeviceTypeId && errors.externalDeviceTypeId ? true : false}
                          helperText={
                            touched.externalDeviceTypeId && errors.externalDeviceTypeId
                              ? errors.externalDeviceTypeId
                              : null
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="equipmentId"
                      label={t("global.id", "ID")}
                      value={values.equipmentId ? values.equipmentId : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.equipmentId && errors.equipmentId ? true : false}
                      helperText={touched.equipmentId && errors.equipmentId ? errors.equipmentId : null}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      id="status"
                      options={equipmentStatuses.data}
                      getOptionLabel={(option) => (option.name ? option.name : "")}
                      value={equipmentStatuses.data.find((i) => i.id === values.status) || ""}
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id
                      }}
                      onChange={(e, value) => {
                        setFieldValue("status", value?.id)
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("global.status", "Status")}
                          variant="outlined"
                          name="status"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.status && errors.status ? true : false}
                          helperText={touched.status && errors.status ? errors.status : null}
                        />
                      )}
                    />
                  </Grid>
                  {editMode && !values.externalDeviceId ? null : (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        autoComplete="off"
                        fullWidth
                        id="externalDeviceId"
                        label={"IoT Device ID"}
                        disabled={values.externalDeviceId !== null}
                        value={values.externalDeviceId ? values.externalDeviceId : ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.externalDeviceId && errors.externalDeviceId ? true : false}
                        helperText={
                          touched.externalDeviceId && errors.externalDeviceId ? errors.externalDeviceId : null
                        }
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  {externalConfigsResult.status === 2 &&
                    (externalConfigsResult.data.length === 1 ? (
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          id="externalConfigId"
                          options={externalConfigsResult?.data}
                          getOptionLabel={(option) => (option.name ? option.name : "")}
                          value={
                            externalConfigsResult?.data?.find((i) => i.id === values.externalConfigId) || ""
                          }
                          isOptionEqualToValue={(option, value) => {
                            return option.id === value.id
                          }}
                          onChange={(e, value) => {
                            setFieldValue("externalConfigId", value?.id)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("global.configuration", "Configuration")}
                              variant="outlined"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.externalConfigId && errors.externalConfigId ? true : false}
                              helperText={
                                touched.externalConfigId && errors.externalConfigId
                                  ? errors.externalConfigId
                                  : null
                              }
                            />
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h5">Connected configuration</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {externalConfigsResult.data[0]?.name}
                        </Typography>
                      </Grid>
                    ))}
                  {/* <Grid item xs={12} md={8}>
                    {equipmentTypesResult.data.findIndex(
                      (i) => i.id === values.equipmentTypeId && i.equipmentTypeName === "Positioning"
                    ) !== -1 && (
                      <Box backgroundColor={theme.palette.grey[100]} p={2} marginBottom={2}>
                        <Box>
                          <Typography variant="h5">{t("global.teltonics", "Teltonics")}</Typography>
                          <Typography variant="bodySmall" marginBottom={2}>
                            {t(
                              "global.specify_which_alarm_types_the_equipment",
                              "Specify which alarm types the equipment has and which AVL ID indicates that it is that alarm type."
                            )}
                          </Typography>
                        </Box>
                        <Box marginY={3} p={2} backgroundColor={theme.palette.background.paper}>
                          <Typography variant="h5">{t("global.add_alarm_type", "Add alarm type")}</Typography>
                          <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Grid item xs={5} sm={7}>
                              <FormControl>
                                <RadioGroup
                                  row
                                  aria-label="alarmType"
                                  name="alarmType"
                                  value={supplement.alarmType}
                                  onChange={(e) => {
                                    const value =
                                      e.target.value === "" ? null : Number.parseInt(e.target.value)
                                    setNewSupplement({ ...supplement, alarmType: value })
                                  }}>
                                  <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label={t("vehiclealarm.alcohol_lock", "Alcohol lock")}
                                  />
                                  <FormControlLabel
                                    value={2}
                                    control={<Radio />}
                                    label={t("global.immobilizer", "Immobilizer")}
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5} sm={3}>
                              <FormControl>
                                <TextField
                                  autoComplete="off"
                                  sx={{ background: theme.palette.background.paper }}
                                  fullWidth
                                  id="supplementavlId"
                                  name="supplementavlId"
                                  label={t("global.avl_id", "AVL ID")}
                                  value={values.supplementavlId}
                                  type="number"
                                  placeholder="0 - 85000"
                                  onChange={(e) => {
                                    setNewSupplement({ ...supplement, avlId: e.target.value })
                                  }}
                                  variant="outlined"
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton
                                onClick={() => addSupplement(setFieldValue, values)}
                                color="primary">
                                <FontAwesomeIcon icon={faPlus} />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                        {values.supplements.map((data, index) => (
                          <Box
                            gap="2px"
                            borderBottom={values.supplements.length - 1 !== index ? 1 : 0}
                            borderColor={theme.palette.grey.light}
                            mb={values.supplements.length - 1 !== index ? 2 : 0}>
                            <Grid
                              container
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center">
                              <Grid item xs={7}>
                                {data.alarmType === 2
                                  ? t("global.immobilizer", "Immobilizer")
                                  : t("vehiclealarm.alcohol_lock", "Alcohol lock")}
                              </Grid>
                              <Grid item xs={3}>
                                {data.avlId}
                              </Grid>
                              <Grid item xs={1}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    removeSupplement(setFieldValue, values, index)
                                  }}
                                  color="primary">
                                  <FontAwesomeIcon variant="contained" icon={faTrash} />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid> */}
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 3 }} />
                    <Typography variant="h4">
                      {t("global.invoicing_documents", "Invoicing documents")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={(e) => setFieldValue("ownedByCustomer", e.target.checked)}
                          checked={values.ownedByCustomer}
                        />
                      }
                      label={t(
                        "global.customer _is_the_owner_of_equipment",
                        "Customer is the owner of equipment"
                      )}
                    />{" "}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      id="companyId"
                      options={companyResult.data}
                      getOptionLabel={(option) => (option.name ? option.name : "")}
                      value={companyResult.data.find((i) => i.id === values.companyId) || ""}
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id
                      }}
                      onChange={(e, value) => {
                        setFieldValue("companyId", value?.id)
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("global.company", "Company")}
                          variant="outlined"
                          name="companyId"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.companyId && errors.companyId ? true : false}
                          helperText={touched.companyId && errors.companyId ? errors.companyId : null}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      value={values?.subscriptionTime !== null ? moment(values?.subscriptionTime) : null}
                      onChange={(value) => {
                        setFieldValue("subscriptionTime", value)
                      }}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          error={touched.subscriptionTime && errors.subscriptionTime ? true : false}
                          helperText={
                            touched.subscriptionTime && errors.subscriptionTime
                              ? errors.subscriptionTime
                              : null
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      autoComplete="off"
                      fullWidths
                      id="equipmentDescription"
                      label={t("global.description", "Description")}
                      value={values.equipmentDescription}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </PaperBox>
            </Grid>
            <Grid item md={4}>
              <PaperBox>
                <Grid item xs={12}>
                  <Typography variant="h4" marginBottom={1}>
                    {t("global.connect_equipment", "Connect Equipment")}
                  </Typography>
                  <Typography variant="bodySmall">
                    {t(
                      "sysadmin.equipment_must_be_connected",
                      "Equipment must be connected to a device or to a storage location"
                    )}
                  </Typography>
                  <FormControlLabel
                    control={<Switch onChange={() => toggleSwitch(setFieldValue)} checked={selectedSwitch} />}
                    label={t("sysadmin.connect_to_unit", "Connect to unit")}
                  />

                  <Box marginTop={2}>
                    {devicesResult.status === 2 ? (
                      <Stack
                        direction="row"
                        spacing={2}
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center">
                        <Autocomplete
                          style={{ width: "100%" }}
                          id="mountedOnDeviceId"
                          options={devicesResult.data}
                          getOptionLabel={(option) => (option.deviceId ? option.deviceId : "")}
                          value={devicesResult.data.find((i) => i.id === values.mountedOnDeviceId) || null}
                          isOptionEqualToValue={(option, value) => {
                            return option.id === value.id
                          }}
                          onChange={(e, value) => {
                            setFieldValue("mountedOnDeviceId", value?.id)
                          }}
                          disabled={!selectedSwitch}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              id="mountedOnDeviceId"
                              label={t("sysadmin.search_unit", "Search unit")}
                              variant="outlined"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              helperText={
                                touched.mountedOnDeviceId && errors.mountedOnDeviceId
                                  ? errors.mountedOnDeviceId
                                  : null
                              }
                              error={touched.mountedOnDeviceId && errors.mountedOnDeviceId ? true : false}
                            />
                          )}
                        />
                        {values.mountedOnDeviceId && (
                          <Box
                            color={theme.palette.primary["main"]}
                            component={NavLink}
                            target="_blank"
                            to={allRoutes.SystemadminDevicesRegisterEdit(values.mountedOnDeviceId)}>
                            <FontAwesomeIcon icon={faSquareUpRight} />
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 56,
                          borderRadius: 1,
                          overflow: "hidden"
                        }}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height="100%"
                          sx={{ position: "absolute", top: 0, left: 0 }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                          <Typography variant="body2" color="text.secondary">
                            {t("global.loading_devices", "Loading devices...")}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box marginTop={2}>
                    <Stack
                      direction="row"
                      spacing={2}
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center">
                      <Autocomplete
                        style={{ width: "100%" }}
                        id="mountedOnStorageId"
                        options={storagesResult.data}
                        getOptionLabel={(option) => (option.name ? option.name : "")}
                        value={storagesResult.data.find((i) => i.id === values.mountedOnStorageId) || null}
                        isOptionEqualToValue={(option, value) => {
                          return option.id === value.id
                        }}
                        onChange={(e, value) => {
                          setFieldValue("mountedOnStorageId", value?.id)
                        }}
                        disabled={selectedSwitch}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("sysadmin.warehoust_location", "Warehouse location")}
                            variant="outlined"
                            name="mountedOnStorageId"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            helperText={
                              touched.mountedOnStorageId && errors.mountedOnStorageId
                                ? errors.mountedOnStorageId
                                : null
                            }
                            error={touched.mountedOnStorageId && errors.mountedOnStorageId ? true : false}
                          />
                        )}
                      />
                      {values.mountedOnStorageId && (
                        <Box
                          color={theme.palette.primary["main"]}
                          component={NavLink}
                          target="_blank"
                          to={allRoutes.SystemadminDevicesStorageEdit(values.mountedOnStorageId)}>
                          <FontAwesomeIcon icon={faSquareUpRight} />
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              </PaperBox>
            </Grid>
          </Grid>
          <Box marginTop={15}></Box>
          <Box sx={{ position: "fixed", bottom: 8, left: 0, right: 15 }} elevation={3}>
            <BottomNavigation
              sx={{ justifyContent: "flex-end", backgroundColor: "transparent", height: "auto" }}>
              <Button color="primary" type="submit" result={result} feedback={feedback}>
                {editMode ? t("global.save", "Save") : t("global.add", "Add")}
              </Button>
            </BottomNavigation>
          </Box>
        </form>
      )}
    </Formik>
  )
}
